import React, { cloneElement, useState } from 'react'

import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { X } from "lucide-react";
import { cn } from '@/lib/utils';
import { TextButton } from '@/components/common/text-button';
import { AddUserIcon } from '@/components/agent-dashboard/assets';
import { CreateForm, CreateFormProps } from './create-form';
import { agentOrderHooks } from '@/manager';
import { AddOrderModalIcon } from './assets';
import { OrderSeatData } from '@/services/agent-v2-order-seat-list';

interface CreateOrderModalProps {
  orderSeatList: OrderSeatData[];
  trigger?: React.ReactElement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSave?: (data) => void;
}

/** 创建订单弹窗 */
export const CreateOrderModal: React.FC<CreateOrderModalProps> = ({
  orderSeatList,
  trigger,
  open: externalOpen,
  onOpenChange,
  onSave,
}) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);

  const {
    createFormData,
    changeValue,
    createOrder,
    clearCreateFormData,
    loading: createLoading,
    emailError,
    goodsIdError
  } = agentOrderHooks.useCreateOrder();

  const { uploadLoading, uploadAttachment } = agentOrderHooks.useUploadOrderAttachment();

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    // 关闭时清空数据
    !open && clearCreateFormData();

    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
  };

  const handleSave = async () => {
    try {
      const success = await createOrder();
      if (!success) return;
      handleClose();
      onSave && onSave({});
    } catch (error) {
      return;
    }
  }

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  // 表单值变化
  const handleValueChange = (data: Parameters<CreateFormProps['onValueChange']>[0]) => {
    changeValue(data.name, data.value);
  }

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[600px] h-[787px]  flex-shrink-0",
          "z-50 overflow-hidden"
        )}>
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-6 pb-4 border-b">
          <Dialog.Title className="text-[rgba(24,29,39,1)] text-lg font-semibold flex items-center">
            <div className='size-12 flex justify-center items-center rounded-lg mr-4 border border-[rgba(213,215,218,1)]'>
              <AddOrderModalIcon className='size-6' />
            </div>
            <span>{t('agent_dashboard.order_management.create_modal.title')}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
              tabIndex={-1}
              onClick={handleClose}
            >
              <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
            </button>
          </Dialog.Close>
        </div>

        {/* 表单区域 */}
        <div className='flex-1 px-6 pt-5 overflow-y-auto'>
          <CreateForm
            {...createFormData}
            emailError={emailError}
            goodsIdError={goodsIdError}
            onValueChange={handleValueChange}
            uploadLoading={uploadLoading}
            uploadAttachment={uploadAttachment}
            orderSeatList={orderSeatList}
          />
        </div>
        {/* 操作按钮区域 */}
        <div className='w-full h-[93px] flex p-6 space-x-3 border-t border-[rgba(233,234,235,1)]'>
          <TextButton
            label={t('agent_dashboard.user_management.create_modal.cancel')}
            className='w-[314px] h-11 bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-semibold text-base !text-[rgba(65,70,81,1)]'
            onClick={handleClose}
          />
          <TextButton
            label={t('agent_dashboard.user_management.create_modal.save')}
            className='w-[314px] h-11'
            onClick={handleSave}
            loading={createLoading}
            disabled={createLoading || uploadLoading}
          />
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}
